package org.example.project.engineslot

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.service.currentSaveArt
import org.example.project.utils.All

@Composable
fun SlotView(art: String = currentSaveArt()) {
    var selectedDate by remember { mutableStateOf(slotDefaultDate()) }
    var config by remember { mutableStateOf(slotDefaultConfig()) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var masterData by remember { mutableStateOf<List<All>>(emptyList()) }
    var zugmaschinen by remember { mutableStateOf<List<All>>(emptyList()) }
    var auflieger by remember { mutableStateOf<List<All>>(emptyList()) }
    var bookings by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }
    var selectedCell by remember { mutableStateOf<All?>(null) }
    var settingsOpen by remember { mutableStateOf(false) }
    var bookingPopup by remember { mutableStateOf<All?>(null) }
    var calendarOpen by remember { mutableStateOf(false) }
    var editMode by remember { mutableStateOf(false) }
    var hoverBooking by remember { mutableStateOf<All?>(null) }
    var hoverX by remember { mutableStateOf(240.0) }
    var hoverY by remember { mutableStateOf(140.0) }
    val scope = rememberCoroutineScope()
    val slotLiveVersion = LocalScreenRefreshVersion.current

    suspend fun syncCurrentDateSlots() {
        val yardFirmenId = SlotSnapCompanyApi.currentFirmenId()
        if (yardFirmenId.isBlank() || selectedStandort.isBlank()) return
        val synced = syncSlotBookings(
            slotEntriesForDate(
                config = config,
                datum = selectedDate,
                standort = selectedStandort,
                existingBookings = bookings
            ).map {
                it.copy(firmenId = yardFirmenId)
            }
        )
        bookings = synced
        selectedCell?.let { current ->
            selectedCell = synced.firstOrNull { it.id == current.id } ?: current
        }
    }

    LaunchedEffect(art, slotLiveVersion) {
        val currentFirmenId = SlotSnapCompanyApi.currentFirmenId()
        masterData = loadSlotBookingMasterData()
        zugmaschinen = loadSlotMeineZugmaschinen()
        auflieger = loadSlotMeineAuflieger()
        standorte = SlotStammdatenApi.loadStandortQuellen()
        bookings = loadSlotBookings(currentFirmenId)
        val initialStandort = slotSelectedStandort(config, standorte)
        selectedStandort = initialStandort
        config = loadSlotConfig(currentFirmenId, initialStandort).copy(standort = initialStandort)
    }

    LaunchedEffect(config, selectedDate, selectedStandort) {
        syncCurrentDateSlots()
    }

    SlotViewLayout(
        selectedDate = selectedDate,
        selectedStandort = selectedStandort,
        selectedCell = selectedCell,
        standorte = standorte,
        config = config,
        bookings = bookings,
        calendarOpen = calendarOpen,
        editMode = editMode,
        onDateChange = {
            selectedDate = it
            selectedCell = null
            calendarOpen = false
        },
        onToggleCalendar = { calendarOpen = !calendarOpen },
        onStandortChange = {
            val nextStandort = it
            selectedStandort = nextStandort
            selectedCell = null
            scope.launch {
                val currentFirmenId = SlotSnapCompanyApi.currentFirmenId()
                config = loadSlotConfig(currentFirmenId, nextStandort).copy(standort = nextStandort)
            }
        },
        onOpenSettings = { settingsOpen = true },
        onToggleEditMode = {
            editMode = !editMode
            selectedCell = null
            hoverBooking = null
        },
        onToggleCellDisabled = { cell ->
            val slotIndex = cell.slotIndex.toIntOrNull() ?: return@SlotViewLayout
            config = slotToggleDisabledCell(config, slotIndex, cell)
            scope.launch {
                val currentFirmenId = SlotSnapCompanyApi.currentFirmenId()
                saveSlotConfig(config, currentFirmenId, selectedStandort)
            }
            selectedCell = null
        },
        onBookingHover = { booking, x, y ->
            hoverBooking = booking
            val (nextX, nextY) = slotHoverOverlayPosition(booking, x, y)
            hoverX = nextX
            hoverY = nextY
        },
        onBookingHoverEnd = { hoverBooking = null },
        onSlotSelect = { cell, _ -> selectedCell = cell },
        onSlotDoubleClick = { _, booking ->
            if (booking != null) bookingPopup = booking
        }
    )

    if (settingsOpen) {
        SlotViewSettingsPopup(
            config = config,
            selectedStandort = selectedStandort,
            standorte = standorte,
            onClose = { settingsOpen = false },
            onSave = { updated ->
                config = updated
                scope.launch {
                    val currentFirmenId = SlotSnapCompanyApi.currentFirmenId()
                    saveSlotConfig(updated, currentFirmenId, selectedStandort)
                    selectedStandort = selectedStandort.ifBlank { slotSelectedStandort(updated, standorte) }
                    settingsOpen = false
                }
            }
        )
    }

    bookingPopup?.let { booking ->
        if (slotIsBooked(booking)) {
            SlotBookingInfoPopup(
                booking = booking,
                masterData = masterData,
                standorte = standorte,
                onClose = { bookingPopup = null }
            )
        } else {
            SlotSetupBookingPopup(
                booking = booking,
                masterData = masterData,
                standorte = standorte,
                zugmaschinen = zugmaschinen,
                auflieger = auflieger,
                showActions = false,
                onClose = { bookingPopup = null },
                onDelete = {
                    scope.launch {
                        saveSlotBooking(booking.copy(zustand = org.example.project.service.ZustandManager.Slot.FREI))
                        bookings = loadSlotBookings(SlotSnapCompanyApi.currentFirmenId())
                        bookingPopup = null
                    }
                },
                onSave = { updated ->
                    scope.launch {
                        runCatching {
                            saveSlotBooking(
                                updated.copy(
                                    zustand = slotBookingStateForSave(updated)
                                )
                            )
                        }.onSuccess {
                            bookings = loadSlotBookings(SlotSnapCompanyApi.currentFirmenId())
                            bookingPopup = null
                        }.onFailure { error ->
                            AppDialogs.alert(error.message ?: "Slot konnte nicht gespeichert werden.")
                        }
                    }
                }
            )
        }
    }

    hoverBooking?.let { booking ->
        SlotBookingHoverOverlay(
            booking = booking,
            masterData = masterData,
            standorte = standorte,
            x = hoverX,
            y = hoverY
        )
    }
}
